import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, BellIcon, CreditCardIcon, TrashIcon, UserIcon, ShieldIcon, CreditCardIcon as PaymentIcon, BellIcon as NotificationIcon, UsersIcon, HandshakeIcon, BuildingIcon, SettingsIcon, ChevronRightIcon, CalendarIcon, StoreIcon, DollarSignIcon, GlobeIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { MultiSelectSearch } from '../../components/UI/MultiSelectSearch';
import { SettingsLayout, SettingsSection, ToggleSwitch, NotificationToggleGroup, PasswordFields, AccountActionsSection, SocialLinksEditor } from '../../components/account';
const VendorAccountSettings = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeSection, setActiveSection] = useState('profile');
  const [formData, setFormData] = useState({
    username: 'bookloverpub',
    displayName: 'BookLover Publishing',
    email: 'vendor@example.com',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    publicProfile: {
      bio: 'We are a small independent publisher specializing in fantasy and science fiction. Our mission is to bring unique voices to the world.',
      location: 'Reading, CA',
      pronouns: 'they/them',
      categories: ['Fantasy', 'Science Fiction', 'Book Boxes'],
      socialLinks: [{
        platform: 'Instagram',
        url: 'https://instagram.com/bookloverpub'
      }, {
        platform: 'Twitter',
        url: 'https://twitter.com/bookloverpub'
      }, {
        platform: 'Website',
        url: 'https://www.bookloverpublishing.com'
      }]
    },
    emailNotifications: {
      orderNotifications: true,
      salesReports: true,
      productReviews: true,
      marketingTips: false
    },
    pushNotifications: {
      orderNotifications: true,
      salesReports: false,
      productReviews: true,
      marketingTips: false
    },
    twoFactorEnabled: true,
    taxInformation: {
      businessName: 'Example Publishing',
      taxId: '12-3456789',
      businessType: 'LLC'
    },
    storeInformation: {
      storeName: 'BookLover Publishing',
      storeDescription: 'Specializing in fantasy and sci-fi novels',
      storeWebsite: 'https://www.bookloverpublishing.com',
      storePhone: '(555) 123-4567',
      storeAddress: '123 Book Street, Reading, CA 90210'
    },
    paymentSettings: {
      acceptStripe: true,
      acceptPaypal: true,
      acceptApplePay: true,
      acceptGooglePay: true,
      acceptAfterpay: false,
      payoutSchedule: 'Weekly',
      collectSalesTax: false
    }
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (type === 'checkbox') {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData({
          ...formData,
          [parent]: {
            ...(formData[parent as keyof typeof formData] as Record<string, boolean>),
            [child]: checked
          }
        });
      } else {
        setFormData({
          ...formData,
          [name]: checked
        });
      }
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (parent === 'publicProfile') {
        setFormData({
          ...formData,
          publicProfile: {
            ...formData.publicProfile,
            [child]: value
          }
        });
      } else if (parent === 'paymentSettings') {
        setFormData({
          ...formData,
          paymentSettings: {
            ...(formData.paymentSettings as any),
            [child]: value
          }
        });
      } else {
        setFormData({
          ...formData,
          [parent]: {
            ...(formData[parent as keyof typeof formData] as Record<string, string>),
            [child]: value
          }
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  const handleCategoryChange = (selected: string[]) => {
    setFormData({
      ...formData,
      publicProfile: {
        ...formData.publicProfile,
        categories: selected
      }
    });
  };
  const handleSocialLinkChange = (index: number, field: 'platform' | 'url', value: string) => {
    const updatedLinks = [...formData.publicProfile.socialLinks];
    updatedLinks[index][field] = value;
    setFormData({
      ...formData,
      publicProfile: {
        ...formData.publicProfile,
        socialLinks: updatedLinks
      }
    });
  };
  const addSocialLink = () => {
    setFormData({
      ...formData,
      publicProfile: {
        ...formData.publicProfile,
        socialLinks: [...formData.publicProfile.socialLinks, {
          platform: '',
          url: ''
        }]
      }
    });
  };
  const removeSocialLink = (index: number) => {
    const updatedLinks = [...formData.publicProfile.socialLinks];
    updatedLinks.splice(index, 1);
    setFormData({
      ...formData,
      publicProfile: {
        ...formData.publicProfile,
        socialLinks: updatedLinks
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Account settings updated successfully!');
  };
  const navigationSections = [{
    id: 'profile',
    label: 'Profile & Password',
    icon: <UserIcon className="h-5 w-5" data-id="element-4411" data-mp="4gccv0dslejn" />
  }, {
    id: 'public-profile',
    label: 'Public Profile',
    icon: <GlobeIcon className="h-5 w-5" data-id="element-4412" data-mp="9fp7w8ethrxb" />
  }, {
    id: 'store',
    label: 'Store Settings',
    icon: <StoreIcon className="h-5 w-5" data-id="element-4413" data-mp="25ik6lsphzmxy" />
  }, {
    id: 'business',
    label: 'Business Information',
    icon: <BuildingIcon className="h-5 w-5" data-id="element-4414" data-mp="20kiq9fjgi1q6" />
  }, {
    id: 'security',
    label: 'Security',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-4415" data-mp="2f9oop1agznx0" />
  }, {
    id: 'payment',
    label: 'Payment Methods',
    icon: <PaymentIcon className="h-5 w-5" data-id="element-4416" data-mp="2o8a2kj4zyerg" />
  }, {
    id: 'payout',
    label: 'Payout Information',
    icon: <DollarSignIcon className="h-5 w-5" data-id="element-4417" data-mp="2k6yj6vcpuprp" />
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: <NotificationIcon className="h-5 w-5" data-id="element-4418" data-mp="2y5eruru52qch" />
  }, {
    id: 'connections',
    label: 'Vendor Connections',
    icon: <UsersIcon className="h-5 w-5" data-id="element-4419" data-mp="2t7wfssktrv09" />
  }, {
    id: 'account',
    label: 'Account Actions',
    icon: <SettingsIcon className="h-5 w-5" data-id="element-4420" data-mp="13uj7ks9ldmtv" />
  }];
  const vendorCategories = ['Book Boxes', 'Merch', 'Romance', 'Fantasy', 'Science Fiction', 'Mystery', 'Thriller', 'Horror', 'Young Adult', "Children's Books", 'Art Prints', 'Clothing', 'Accessories', 'Stationery', 'Home Decor'];
  const vendorConnections = [{
    title: 'Connect with Vendors',
    description: 'Find and connect with other vendors for collaborations and partnerships',
    link: '/vendors/connect',
    icon: <UsersIcon className="h-5 w-5 text-purple-500" data-id="element-4421" data-mp="2rp66zq6kyjks" />
  }, {
    title: 'Partnership Opportunities',
    description: 'Explore available partnership opportunities with other vendors',
    link: '/vendors/opportunities',
    icon: <HandshakeIcon className="h-5 w-5 text-purple-500" data-id="element-4422" data-mp="2huea6okto0r2" />
  }, {
    title: 'Vendor Directory',
    description: 'Browse the complete directory of vendors on our platform',
    link: '/vendors/directory',
    icon: <BuildingIcon className="h-5 w-5 text-purple-500" data-id="element-4423" data-mp="kzhu6vxvob95" />
  }, {
    title: 'Event Opportunities',
    description: 'Find events where you can showcase your products',
    link: '/vendors/events',
    icon: <CalendarIcon className="h-5 w-5 text-purple-500" data-id="element-4424" data-mp="2zwfsjse4cdqd" />
  }];
  const inputClass = `mt-1 block w-full rounded-md border sm:text-sm focus:outline-none focus:ring-2 px-3 py-2 ${rt.inputFocus} ${dk.input}`;
  const labelClass = `block text-sm font-medium ${dk.textSecondary}`;
  const saveSections = ['profile', 'public-profile', 'store', 'business', 'security', 'payment', 'payout', 'notifications'];
  return <SettingsLayout title="Vendor Settings" subtitle="Manage your business account details, preferences, and connections" headerIcon={<StoreIcon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-4426" data-mp="278toyk4w593i" />} navigationSections={navigationSections} activeSection={activeSection} setActiveSection={setActiveSection} onSubmit={handleSubmit} showSaveButton={saveSections.includes(activeSection)} data-id="element-4425" data-mp="2h8aun1k57mzh">
      {/* Profile & Password */}
      {activeSection === 'profile' && <SettingsSection icon={<UserIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4428" data-mp="hyrci98uuz7z" />} title="Account Information" subtitle="Your login credentials and password" data-id="element-4427" data-mp="cy3lhnkmmidr">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4429" data-mp="msc1xcwdukvk">
            <div data-id="element-4430" data-mp="rnrhc9a1gou3">
              <label htmlFor="username" className={labelClass} data-id="element-4431" data-mp="rob0ftbfdj9p">
                Username
              </label>
              <input type="text" id="username" name="username" value={formData.username} disabled className={`${inputClass} opacity-70 cursor-not-allowed`} data-id="element-4432" data-mp="3q92op3g6pcjy" />
            </div>
            <div data-id="element-4433" data-mp="8zw892pdfz7b">
              <label htmlFor="email" className={labelClass} data-id="element-4434" data-mp="8w8eq3jxfcsl">
                Email
              </label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} data-id="element-4435" data-mp="1kdq8ca7smw1e" />
            </div>
          </div>
          <div className={`pt-6 border-t ${dk.border}`} data-id="element-4436" data-mp="wr04qwldjags">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4437" data-mp="11kulps9lhbc6">
              Change Password
            </h4>
            <PasswordFields formData={formData} onChange={handleInputChange} data-id="element-4438" data-mp="16iectsre5ilj" />
          </div>
        </SettingsSection>}

      {/* Public Profile */}
      {activeSection === 'public-profile' && <SettingsSection icon={<GlobeIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4440" data-mp="itbjhnnea1b7" />} title="Public Profile" subtitle="This information will be visible to readers on your vendor profile" data-id="element-4439" data-mp="duihardk5wqc">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4441" data-mp="nq22aiql3b0b">
            <div className="space-y-4" data-id="element-4442" data-mp="1u7ytg502o3rn">
              <div data-id="element-4443" data-mp="1z7lbcrqui86s">
                <label htmlFor="displayName" className={labelClass} data-id="element-4444" data-mp="1z6sau8ihcex3">
                  Display Name
                </label>
                <input type="text" id="displayName" name="displayName" value={formData.displayName} onChange={handleInputChange} className={inputClass} data-id="element-4445" data-mp="3joayeqado78i" />
              </div>
              <div data-id="element-4446" data-mp="34zybeit9pyq5">
                <label htmlFor="pronouns" className={labelClass} data-id="element-4447" data-mp="350g4x7ojpyud">
                  Pronouns (Optional)
                </label>
                <input type="text" id="pronouns" name="publicProfile.pronouns" value={formData.publicProfile.pronouns} onChange={handleInputChange} placeholder="e.g. they/them" className={inputClass} data-id="element-4448" data-mp="2w01ha1o407np" />
              </div>
              <div data-id="element-4449" data-mp="p921h9tscvm0">
                <label htmlFor="location" className={labelClass} data-id="element-4450" data-mp="p77p4jwkybtj">
                  Location
                </label>
                <input type="text" id="location" name="publicProfile.location" value={formData.publicProfile.location} onChange={handleInputChange} placeholder="e.g. London, UK" className={inputClass} data-id="element-4451" data-mp="2eqpck1l0eix5" />
              </div>
            </div>
            <div className={`p-5 rounded-xl border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4452" data-mp="29p9tug6b0ohx">
              <label className={`${labelClass} mb-3`} data-id="element-4453" data-mp="32g9x2owt9o8j">Profile Picture</label>
              <div className="flex flex-col items-center" data-id="element-4454" data-mp="32e7a6dqygb81">
                <div className={`h-20 w-20 rounded-full flex items-center justify-center mb-3 ${rt.bgMedium}`} data-id="element-4455" data-mp="2ofh448m5r17j">
                  <UserIcon className={`h-10 w-10 ${rt.textPrimary}`} data-id="element-4456" data-mp="2sjymq4gs78gq" />
                </div>
                <button type="button" className={`px-4 py-2 text-sm font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4457" data-mp="1d3g6s5b8a4g6">
                  Upload Image
                </button>
              </div>
            </div>
          </div>
          <div data-id="element-4458" data-mp="14jtep9prcrex">
            <label htmlFor="bio" className={labelClass} data-id="element-4459" data-mp="14mfntdsarez9">
              Bio / About
            </label>
            <textarea id="bio" name="publicProfile.bio" rows={4} value={formData.publicProfile.bio} onChange={handleInputChange} placeholder="Tell readers about your business..." className={inputClass} data-id="element-4460" data-mp="1jfj3ffmbvinc" />
          </div>
          <div data-id="element-4461" data-mp="10ihph5asq879">
            <MultiSelectSearch label="What do you sell / specialize in?" options={vendorCategories} selected={formData.publicProfile.categories} onChange={handleCategoryChange} placeholder="Search categories..." data-id="element-4462" data-mp="10izqsgcbfcm1" />
            <p className={`mt-1 text-xs ${dk.textFaint}`} data-id="element-4463" data-mp="345gijt570pp">
              Select tags that describe your business.
            </p>
          </div>
          <SocialLinksEditor links={formData.publicProfile.socialLinks} platformOptions={['Instagram', 'Twitter', 'TikTok', 'Website', 'Facebook', 'Other']} onLinkChange={handleSocialLinkChange} onAdd={addSocialLink} onRemove={removeSocialLink} data-id="element-4464" data-mp="32j7i69o0ebbj" />
        </SettingsSection>}

      {/* Store Settings */}
      {activeSection === 'store' && <SettingsSection icon={<StoreIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4466" data-mp="3u8apxzf3u3sx" />} title="Store Information" subtitle="Configure your storefront details" data-id="element-4465" data-mp="3q8s6l82xm0s7">
          <div data-id="element-4467" data-mp="2zvgp955xhvi">
            <label htmlFor="storeName" className={labelClass} data-id="element-4468" data-mp="1zurmk8ye7qzp">
              Store Name
            </label>
            <input type="text" id="storeName" name="storeInformation.storeName" value={formData.storeInformation.storeName} onChange={handleInputChange} className={inputClass} data-id="element-4469" data-mp="24t2tobxazne8" />
          </div>
          <div data-id="element-4470" data-mp="1nk0lyjdo162v">
            <label htmlFor="storeDescription" className={labelClass} data-id="element-4471" data-mp="1njihizgunljv">
              Store Description
            </label>
            <textarea id="storeDescription" name="storeInformation.storeDescription" value={formData.storeInformation.storeDescription} onChange={handleInputChange} rows={3} className={inputClass} data-id="element-4472" data-mp="1imi94hwj26pl" />
          </div>
          <div data-id="element-4473" data-mp="2696xnmg1kxf6">
            <label htmlFor="storeWebsite" className={labelClass} data-id="element-4474" data-mp="26curyntb9nuy">
              Website
            </label>
            <input type="url" id="storeWebsite" name="storeInformation.storeWebsite" value={formData.storeInformation.storeWebsite} onChange={handleInputChange} className={inputClass} data-id="element-4475" data-mp="22bixo4zhsegf" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4476" data-mp="s9c5yl6co3wr">
            <div data-id="element-4477" data-mp="x8yg2lqvt41c">
              <label htmlFor="storePhone" className={labelClass} data-id="element-4478" data-mp="x9hz65s9pygy">
                Phone Number
              </label>
              <input type="tel" id="storePhone" name="storeInformation.storePhone" value={formData.storeInformation.storePhone} onChange={handleInputChange} className={inputClass} data-id="element-4479" data-mp="ifd4loe3oo5v" />
            </div>
          </div>
          <div data-id="element-4480" data-mp="3vsf8ci8fstv1">
            <label htmlFor="storeAddress" className={labelClass} data-id="element-4481" data-mp="3vsoprhff1sp4">
              Business Address
            </label>
            <input type="text" id="storeAddress" name="storeInformation.storeAddress" value={formData.storeInformation.storeAddress} onChange={handleInputChange} className={inputClass} data-id="element-4482" data-mp="4mdtrzp9ji4n" />
          </div>
          <div className={`pt-6 border-t ${dk.border}`} data-id="element-4483" data-mp="385bbginy5b4s">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4484" data-mp="335oymac15wwi">
              Store Appearance
            </h4>
            <div className={`p-5 rounded-xl border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4485" data-mp="3h69h15e19qph">
              <p className={`text-sm mb-4 ${dk.textMuted}`} data-id="element-4486" data-mp="3c5c1l0keraro">
                Upload your store logo and banner to personalize your store's
                appearance
              </p>
              <div className="flex justify-center gap-6" data-id="element-4487" data-mp="3m389lji1tplk">
                <div className="text-center" data-id="element-4488" data-mp="3qag9vfrhv0d">
                  <div className={`h-20 w-20 rounded-lg mb-2 flex items-center justify-center ${dk.isDark ? 'bg-gray-600' : 'bg-gray-200'}`} data-id="element-4489" data-mp="3p8v2y517s4x">
                    <UserIcon className={`h-8 w-8 ${dk.textFaint}`} data-id="element-4490" data-mp="dnokmwoeutcb" />
                  </div>
                  <button type="button" className={`px-3 py-1.5 text-xs font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4491" data-mp="8pwsc1jut57l">
                    Upload Logo
                  </button>
                </div>
                <div className="text-center" data-id="element-4492" data-mp="1k5iid9q5qnvc">
                  <div className={`h-20 w-40 rounded-lg mb-2 flex items-center justify-center ${dk.isDark ? 'bg-gray-600' : 'bg-gray-200'}`} data-id="element-4493" data-mp="1p28y1vy1vhg8">
                    <span className={`text-xs ${dk.textFaint}`} data-id="element-4494" data-mp="we4sdafhb4dj">
                      Banner Image
                    </span>
                  </div>
                  <button type="button" className={`px-3 py-1.5 text-xs font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4495" data-mp="11dhajhvenn9s">
                    Upload Banner
                  </button>
                </div>
              </div>
            </div>
          </div>
        </SettingsSection>}

      {/* Business Information */}
      {activeSection === 'business' && <SettingsSection icon={<BuildingIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4497" data-mp="b3gwv1hrqod7" />} title="Tax Information" subtitle="Your business and tax details" data-id="element-4496" data-mp="ik3pc4hhuv7d">
          <div data-id="element-4498" data-mp="61rj3e35vwlf">
            <label htmlFor="businessName" className={labelClass} data-id="element-4499" data-mp="14rcn22egrts">
              Business Name
            </label>
            <input type="text" id="businessName" name="taxInformation.businessName" value={formData.taxInformation.businessName} onChange={handleInputChange} className={inputClass} data-id="element-4500" data-mp="3sbke8713usyu" />
          </div>
          <div data-id="element-4501" data-mp="3eje9dh3ek8ep">
            <label htmlFor="taxId" className={labelClass} data-id="element-4502" data-mp="3eiusm3phnq29">
              Tax ID / EIN
            </label>
            <input type="text" id="taxId" name="taxInformation.taxId" value={formData.taxInformation.taxId} onChange={handleInputChange} className={inputClass} data-id="element-4503" data-mp="39lm7bu91n5ws" />
          </div>
          <div data-id="element-4504" data-mp="1pg6f9srn16n4">
            <label htmlFor="businessType" className={labelClass} data-id="element-4505" data-mp="1pgol9h43qz88">
              Business Type
            </label>
            <select id="businessType" name="taxInformation.businessType" value={formData.taxInformation.businessType} onChange={handleInputChange} className={inputClass} data-id="element-4506" data-mp="1kier99u464o7">
              <option value="Sole Proprietorship" data-id="element-4507" data-mp="16hkdscs7ldio">Sole Proprietorship</option>
              <option value="LLC" data-id="element-4508" data-mp="16jnebqqzmp1n">LLC</option>
              <option value="Corporation" data-id="element-4509" data-mp="11qc8nsxwwebb">Corporation</option>
              <option value="Partnership" data-id="element-4510" data-mp="11p12jmgzuuh1">Partnership</option>
              <option value="Non-profit" data-id="element-4511" data-mp="wskgcxtcuq6l">Non-profit</option>
            </select>
          </div>
        </SettingsSection>}

      {/* Security */}
      {activeSection === 'security' && <SettingsSection icon={<ShieldIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4513" data-mp="nt5nxstmo5jg" />} title="Security Settings" subtitle="Manage your security options" data-id="element-4512" data-mp="nprlkkkyrwc0">
          <div className="flex items-center justify-between" data-id="element-4514" data-mp="2uea4rz600bgf">
            <div data-id="element-4515" data-mp="2zeg1vuv1eq5w">
              <h4 className={`text-sm font-semibold ${dk.textHeading}`} data-id="element-4516" data-mp="2zdwp0sk8uryq">
                Two-Factor Authentication
              </h4>
              <p className={`text-sm ${dk.textMuted}`} data-id="element-4517" data-mp="34e0wxu736edc">
                Add an extra layer of security to your account
              </p>
            </div>
            <ToggleSwitch name="twoFactorEnabled" checked={formData.twoFactorEnabled} onChange={handleInputChange} data-id="element-4518" data-mp="2bp3p6l0i3zyu" />
          </div>
          <div className={`border-t pt-6 ${dk.border}`} data-id="element-4519" data-mp="2kpjum00t1h6c">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4520" data-mp="2knz0ucmbe29j">
              Login History
            </h4>
            <div className={`rounded-lg overflow-hidden border ${dk.border}`} data-id="element-4521" data-mp="2picyymsd2nhw">
              {[{
            time: 'Today at 10:32 AM',
            detail: 'San Francisco, CA • Chrome on Windows',
            current: true
          }, {
            time: 'Yesterday at 8:12 PM',
            detail: 'San Francisco, CA • Chrome on Windows',
            current: false
          }, {
            time: 'June 15, 2023 at 3:45 PM',
            detail: 'San Francisco, CA • Safari on macOS',
            current: false
          }].map(({
            time,
            detail,
            current
          }, i) => <div key={time} className={`flex justify-between items-center px-4 py-3 ${i > 0 ? `border-t ${dk.border}` : ''} ${dk.surfaceMuted}`} data-id="element-4522" data-mp="2hdtyli1eanlx" data-mpi={i}>
                  <div data-id="element-4523" data-mp="2mb3oeysp51aa">
                    <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4524" data-mp="2mau7s1tjk6h9">
                      {time}
                    </p>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-4525" data-mp="5kvfthf8ugnt">{detail}</p>
                  </div>
                  {current && <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-4526" data-mp="1n7he4ece5r6">
                      Current
                    </span>}
                </div>)}
            </div>
          </div>
        </SettingsSection>}

      {/* Payment Methods */}
      {activeSection === 'payment' && <SettingsSection icon={<CreditCardIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4528" data-mp="47dm4eas608s" />} title="Payment Settings" subtitle="Configure payment methods and payouts" data-id="element-4527" data-mp="462gsa1oskfk">
          <div data-id="element-4529" data-mp="wsnbnkhdfenv">
            <h4 className={`text-sm font-semibold mb-1 ${dk.textHeading}`} data-id="element-4530" data-mp="wt6paozzngw3">
              Accepted Payment Methods
            </h4>
            <p className={`text-sm mb-4 ${dk.textMuted}`} data-id="element-4531" data-mp="n3vl9gryl2f2">
              Select which payment methods you want to accept in your store.
            </p>
            <div className={`rounded-lg border overflow-hidden ${dk.border}`} data-id="element-4532" data-mp="1as6h25issbwh">
              {[{
            id: 'acceptStripe',
            label: 'Credit/Debit Cards (Stripe)',
            icon: <CreditCardIcon className="h-5 w-5 text-indigo-500" data-id="element-4533" data-mp="3nzfqgqfa9qkw" />
          }, {
            id: 'acceptPaypal',
            label: 'PayPal',
            icon: <span className="font-bold text-blue-600 italic px-1" data-id="element-4534" data-mp="3e55yh9u96uap">
                      P
                    </span>
          }, {
            id: 'acceptApplePay',
            label: 'Apple Pay',
            icon: <span className="font-bold text-gray-900 px-1" data-id="element-4535" data-mp="5o1g50q1el56"></span>
          }, {
            id: 'acceptGooglePay',
            label: 'Google Pay',
            icon: <span className="font-bold text-blue-500 px-1" data-id="element-4536" data-mp="3s2cc6q04e2t2">G</span>
          }, {
            id: 'acceptAfterpay',
            label: 'Afterpay / Klarna',
            icon: <span className="font-bold text-green-600 px-1" data-id="element-4537" data-mp="okarhg9k0xlh">A</span>
          }].map(method => <div key={method.id} className={`flex items-center justify-between px-4 py-3.5 border-b last:border-0 ${dk.border} ${dk.surfaceMuted}`} data-id="element-4538" data-mp="10vuzm912ec1i">
                  <div className="flex items-center" data-id="element-4539" data-mp="1avu865xhi2ed">
                    <div className={`p-2 rounded-md mr-3 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-4540" data-mp="1asg6z10k3z8i">
                      {method.icon}
                    </div>
                    <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-4541" data-mp="1om7ds1mwl50j">
                      {method.label}
                    </span>
                  </div>
                  <ToggleSwitch name={`paymentSettings.${method.id}`} checked={(formData.paymentSettings as any)[method.id]} onChange={handleInputChange} data-id="element-4542" data-mp="1tkio0dh41hja" />
                </div>)}
            </div>
          </div>

          <div className={`border-t pt-8 ${dk.border}`} data-id="element-4543" data-mp="1dvm40d9hjntz">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4544" data-mp="18x1289el4w3f">
              Payout Settings
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4545" data-mp="1nmp1z674bmp1">
              <div data-id="element-4546" data-mp="1iu5n85wp2lin">
                <label htmlFor="payoutSchedule" className={labelClass} data-id="element-4547" data-mp="1itnkcqfj191r">
                  Payout Schedule
                </label>
                <select id="payoutSchedule" name="paymentSettings.payoutSchedule" value={formData.paymentSettings.payoutSchedule} onChange={handleInputChange} className={inputClass} data-id="element-4548" data-mp="1wnwx5fec87ew">
                  <option value="Weekly" data-id="element-4549" data-mp="21lon43vf9jgg">Weekly (Every Friday)</option>
                  <option value="Bi-weekly" data-id="element-4550" data-mp="c70gkj9iivqc">
                    Bi-weekly (Every other Friday)
                  </option>
                  <option value="Monthly" data-id="element-4551" data-mp="78qi9zshrf0i">Monthly (Last day of month)</option>
                </select>
                <p className={`mt-1 text-xs ${dk.textFaint}`} data-id="element-4552" data-mp="2iv100r8oeei3">
                  Choose how often earnings are transferred to your bank
                  account.
                </p>
              </div>
              <div className="flex items-center pt-6" data-id="element-4553" data-mp="29465kzla08lk">
                <input id="collectSalesTax" name="paymentSettings.collectSalesTax" type="checkbox" checked={formData.paymentSettings.collectSalesTax} onChange={handleInputChange} className={`h-4 w-4 border-gray-300 rounded cursor-pointer ${rt.checkbox}`} data-id="element-4554" data-mp="2e2r3gsd22g6u" />
                <label htmlFor="collectSalesTax" className={`ml-2 block text-sm ${dk.textSecondary}`} data-id="element-4555" data-mp="1v4ex1edj3key">
                  Automatically collect sales tax
                </label>
              </div>
            </div>
          </div>

          <div className={`border-t pt-8 ${dk.border}`} data-id="element-4556" data-mp="2cbtkxcbelmru">
            <div className="flex items-center justify-between mb-4" data-id="element-4557" data-mp="2cbbg3raoe08b">
              <h4 className={`text-sm font-semibold ${dk.textHeading}`} data-id="element-4558" data-mp="289zmlaookuuu">
                Linked Bank Account
              </h4>
              <button type="button" className={`px-4 py-2 text-sm font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4559" data-mp="23hyf022gjsb5">
                Update Account
              </button>
            </div>
            <div className={`rounded-lg p-4 flex items-center justify-between border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4560" data-mp="1tiz4sf21nom1">
              <div className="flex items-center" data-id="element-4561" data-mp="1klot4slbdxgl">
                <div className={`p-2 rounded-full mr-3 ${dk.isDark ? 'bg-gray-700' : 'bg-white'}`} data-id="element-4562" data-mp="1kmi1fxzx8v0u">
                  <BuildingIcon className={`h-5 w-5 ${dk.textSecondary}`} data-id="element-4563" data-mp="3eyy64ud2k13l" />
                </div>
                <div data-id="element-4564" data-mp="39z1y8k193pir">
                  <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4565" data-mp="3a0wnubngw5t6">
                    Bank of America — Checking
                  </p>
                  <p className={`text-xs ${dk.textMuted}`} data-id="element-4566" data-mp="3hh9w67otwzpk">
                    Account ending in 5678
                  </p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium bg-green-100 text-green-800`} data-id="element-4567" data-mp="3r9fqho2jzt7x">
                Verified
              </span>
            </div>
          </div>
        </SettingsSection>}

      {/* Payout Information */}
      {activeSection === 'payout' && <SettingsSection icon={<DollarSignIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4569" data-mp="38hmwbqbr01ym" />} title="Payout Information" subtitle="Manage your bank accounts and payout schedule" headerRight={<button type="button" className={`px-4 py-2 text-sm font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4570" data-mp="nrfbvq2hsplc">
              Add Bank Account
            </button>} data-id="element-4568" data-mp="38hwnhh8j4f5w">
          <div className={`rounded-lg p-4 flex items-center justify-between border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4571" data-mp="1iwdfl24tniua">
            <div className="flex items-center" data-id="element-4572" data-mp="196btgfw14ai0">
              <BuildingIcon className={`h-6 w-6 mr-3 ${dk.textFaint}`} data-id="element-4573" data-mp="195ikd89ll8wp" />
              <div data-id="element-4574" data-mp="1e3tvrnfjjrh1">
                <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4575" data-mp="1e3ajoncknxax">
                  Bank of America - Checking
                </p>
                <p className={`text-xs ${dk.textMuted}`} data-id="element-4576" data-mp="15473ps8pby0n">
                  Account ending in 5678
                </p>
              </div>
            </div>
            <div className="flex space-x-3" data-id="element-4577" data-mp="q8hbvdgu4qo0">
              <button type="button" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-4578" data-mp="v60yp1rlmbrk">
                Edit
              </button>
              <button type="button" className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-4579" data-mp="2kiuv833ryrsd">
                Remove
              </button>
            </div>
          </div>
          <div data-id="element-4580" data-mp="8xvnvubjjyav">
            <h4 className={`text-sm font-semibold mb-3 ${dk.textHeading}`} data-id="element-4581" data-mp="8ydhej6tjyf3">
              Payout Schedule
            </h4>
            <select className={inputClass} data-id="element-4582" data-mp="nn9uiunqg1mk">
              <option data-id="element-4583" data-mp="ivrxuisqsn7e">Weekly - Every Friday</option>
              <option data-id="element-4584" data-mp="iv01h5rbmlg2">Bi-weekly - Every other Friday</option>
              <option data-id="element-4585" data-mp="wmwrn49fil9t">Monthly - Last day of month</option>
            </select>
          </div>
        </SettingsSection>}

      {/* Notifications */}
      {activeSection === 'notifications' && <SettingsSection icon={<BellIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4587" data-mp="16k1ez7f0gms7" />} title="Notification Preferences" subtitle="Control how you receive notifications" data-id="element-4586" data-mp="16lw0amu72gwt">
          <NotificationToggleGroup groups={[{
        key: 'email',
        label: 'Email Notifications',
        fields: [{
          id: 'email-order-notifications',
          name: 'emailNotifications.orderNotifications',
          checked: formData.emailNotifications.orderNotifications,
          label: 'Order notifications'
        }, {
          id: 'email-sales-reports',
          name: 'emailNotifications.salesReports',
          checked: formData.emailNotifications.salesReports,
          label: 'Weekly sales reports'
        }, {
          id: 'email-product-reviews',
          name: 'emailNotifications.productReviews',
          checked: formData.emailNotifications.productReviews,
          label: 'Product reviews'
        }, {
          id: 'email-marketing-tips',
          name: 'emailNotifications.marketingTips',
          checked: formData.emailNotifications.marketingTips,
          label: 'Marketing tips and platform updates'
        }]
      }, {
        key: 'push',
        label: 'Push Notifications',
        fields: [{
          id: 'push-order-notifications',
          name: 'pushNotifications.orderNotifications',
          checked: formData.pushNotifications.orderNotifications,
          label: 'Order notifications'
        }, {
          id: 'push-sales-reports',
          name: 'pushNotifications.salesReports',
          checked: formData.pushNotifications.salesReports,
          label: 'Weekly sales reports'
        }, {
          id: 'push-product-reviews',
          name: 'pushNotifications.productReviews',
          checked: formData.pushNotifications.productReviews,
          label: 'Product reviews'
        }, {
          id: 'push-marketing-tips',
          name: 'pushNotifications.marketingTips',
          checked: formData.pushNotifications.marketingTips,
          label: 'Marketing tips and platform updates'
        }]
      }]} onChange={handleInputChange} data-id="element-4588" data-mp="38bxne85vtgir" />
        </SettingsSection>}

      {/* Vendor Connections */}
      {activeSection === 'connections' && <SettingsSection icon={<UsersIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4590" data-mp="3fa3jrri5cvup" />} title="Vendor Connections" subtitle="Connect with other vendors to collaborate and grow" data-id="element-4589" data-mp="3f9ab2kzmnw9x">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-4591" data-mp="auk8gn3sa6rg">
            {vendorConnections.map((connection, index) => <div key={index} className={`border rounded-xl overflow-hidden hover:shadow-md transition-shadow ${dk.card}`} data-id="element-4592" data-mp="fmlswwb3v3sk" data-mpi={index}>
                <div className="p-4" data-id="element-4593" data-mp="1vdo31tz6nahm">
                  <div className="flex items-start" data-id="element-4594" data-mp="1vcla7giauxz2">
                    <div className={`flex-shrink-0 p-2 rounded-lg ${rt.bgMedium}`} data-id="element-4595" data-mp="2a1r5wuhxi627">
                      {connection.icon}
                    </div>
                    <div className="ml-3" data-id="element-4596" data-mp="1hd3hiq1vv0kf">
                      <h4 className={`text-base font-medium ${dk.textPrimary}`} data-id="element-4597" data-mp="1he4wv6pxm97y">
                        {connection.title}
                      </h4>
                      <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-4598" data-mp="1rb9in5fv82o8">
                        {connection.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-4599" data-mp="32qoda2klqig6">
                    <Link to={connection.link} className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4600" data-mp="32pdb276t1ir6">
                      Go to {connection.title}
                      <ChevronRightIcon className="ml-1 h-4 w-4" data-id="element-4601" data-mp="39tml563425gi" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>
          <div className={`p-5 rounded-xl border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4602" data-mp="3js2bv7y87cph">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4603" data-mp="2m4bo9zovsv7n">
              Recent Connection Requests
            </h4>
            <div className="space-y-3" data-id="element-4604" data-mp="2r4g7vyl36623">
              {[{
            name: 'Bookish Designs',
            desc: 'Book cover design services'
          }, {
            name: 'Literary Merch Co.',
            desc: 'Book-themed merchandise'
          }].map(({
            name,
            desc
          }) => <div key={name} className={`flex justify-between items-center p-3 rounded-lg border ${dk.border}`} data-id="element-4605" data-mp="28cni0d1bd0yl">
                  <div className="flex items-center" data-id="element-4606" data-mp="pfol7lhyq5aj">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${dk.isDark ? 'bg-gray-600' : 'bg-gray-200'}`} data-id="element-4607" data-mp="lh8ryzvag0oo">
                      <UserIcon className={`h-5 w-5 ${dk.textFaint}`} data-id="element-4608" data-mp="bijc1cmycqb2" />
                    </div>
                    <div className="ml-3" data-id="element-4609" data-mp="6jgh4l0os0gt">
                      <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4610" data-mp="6k7zgu59w1p5">
                        {name}
                      </p>
                      <p className={`text-xs ${dk.textMuted}`} data-id="element-4611" data-mp="3tw0nahew39aa">{desc}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-4612" data-mp="3oxftp0u57nut">
                    <button className={`px-3 py-1.5 text-xs font-medium rounded-lg ${rt.primaryButton}`} data-id="element-4613" data-mp="3outdydx3m21i">
                      Accept
                    </button>
                    <button className={`px-3 py-1.5 text-xs font-medium rounded-lg ${dk.badgeNeutral}`} data-id="element-4614" data-mp="3f12rtfog23oz">
                      Decline
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </SettingsSection>}

      {/* Account Actions */}
      {activeSection === 'account' && <AccountActionsSection data-id="element-4615" data-mp="12qjfrl9msn94" />}
    </SettingsLayout>;
};
export default VendorAccountSettings;